import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button, Card, Chip, EmptyState, Input, Modal, Skeleton, baht } from "../ui";
import Wizard, { Counted, Soon } from "./Wizard";
import ImageUploader from "./ImageUploader";
import TagFilter from "../character/TagFilter";
import { useAsync } from "../../hooks";
import { useToast } from "../../context/ToastContext";
import content from "../../services/api-content";
import api from "../../services/api";
import { PERSONAS } from "../../services/ai/persona";
import "../../styles/content.css";

const STEPS = [
  { key: "basic", title: "ข้อมูลพื้นฐาน" },
  { key: "images", title: "รูปโปรไฟล์" },
  { key: "personality", title: "บุคลิกและความสัมพันธ์" },
  { key: "greeting", title: "ข้อความแรก" },
  { key: "scene", title: "ฉากและบทบาท" },
  { key: "tags", title: "แท็ก" },
  { key: "privacy", title: "ความเป็นส่วนตัว" },
  { key: "advanced", title: "ขั้นสูง" },
];
const ROMANTIC = [[0, "เฉยๆ"], [100, "หวั่นไหว"], [300, "คนคุย"], [600, "แฟน"], [900, "แต่งงาน"]];
const INTIMACY = [[-1000, "คู่อริ"], [-200, "เกลียด"], [0, "คนรู้จัก"], [100, "เพื่อน"], [300, "เพื่อนสนิท"], [700, "เพื่อนตาย"]];
const FLAGS = [["white", "ธงขาว", "อบอุ่น ปลอดภัย ทุกวัย"], ["green", "ธงเขียว", "เป็นมิตร มีดราม่าเล็กน้อย"], ["yellow", "ธงเหลือง", "นิสัยซับซ้อน ปากร้าย ขี้หึง"], ["red", "ธงแดง", "ตัวร้าย/อันตรายในเรื่องแต่ง (ไม่ใช่เนื้อหา 18+)"]];
const LANGS = [["th", "ไทย"], ["en", "English"], ["ja", "日本語"], ["ko", "한국어"], ["zh", "中文"], ["vi", "Tiếng Việt"]];
const VIS = [["public", "สาธารณะ", "ทุกคนค้นเจอและเริ่มแชทได้"], ["unlisted", "ไม่แสดงในรายการ", "เฉพาะคนที่มีลิงก์/โค้ดแชร์"], ["private", "ส่วนตัว", "เฉพาะคุณ"]];
const FLAG_FROM_AI = { white: "white", green: "green", yellow: "yellow", red: "red" };

const EMPTY = {
  id: null, name: "", tagline: "", description: "", bio: "", greeting: "", greetingImage: "", images: [], tags: [], flag: "green", baseLanguage: "th", visibility: "public", persona: "",
  romantic: 0, intimacy: 0, introversion: "ambivert", empathy: 50, loveFocus: 50, loyalty: 50, defaultPersonaId: "", defaultScenarioId: "",
};

const fromCharacter = (c) => ({
  ...EMPTY, id: c.id, name: c.name || "", tagline: c.tagline || "", description: c.description || "", bio: c.bio || "", greeting: c.greeting || "", greetingImage: c.greetingImage || "",
  images: [c.image, ...(c.images || [])].filter(Boolean).filter((v, i, a) => a.indexOf(v) === i).slice(0, 5), tags: (c.tags || []).filter((t) => t !== "PG"), flag: c.flag || "green",
  baseLanguage: c.baseLanguage || "th", visibility: c.visibility || "public", persona: c.persona || "",
  romantic: Number(c.initialRelationship?.romanticValue) || 0, intimacy: Number(c.initialRelationship?.intimacyValue) || 0,
  introversion: c.personality?.introversion || "ambivert", empathy: c.personality?.empathy ?? 50, loveFocus: c.personality?.loveFocus ?? 50, loyalty: c.personality?.loyalty ?? 50,
  defaultPersonaId: c.defaultPersonaId || "", defaultScenarioId: c.defaultScenarioId || "",
});

const toPayload = (f, draft) => ({
  ...(f.id ? { id: f.id } : {}),
  name: f.name.trim(), tagline: f.tagline.trim(), description: f.description, bio: f.bio, greeting: f.greeting, greetingImage: f.greetingImage,
  image: f.images[0] || "", images: f.images.slice(0, 5), tags: f.tags.slice(0, 12), flag: f.flag, baseLanguage: f.baseLanguage, visibility: f.visibility, persona: f.persona || "",
  initialRelationship: { romanticValue: f.romantic, intimacyValue: f.intimacy },
  personality: { introversion: f.introversion, empathy: f.empathy, loveFocus: f.loveFocus, loyalty: f.loyalty },
  defaultPersonaId: f.defaultPersonaId || null, defaultScenarioId: f.defaultScenarioId || null,
  draft,
});

const levelOf = (levels, v) => levels.reduce((acc, l) => (v >= l[0] ? l[1] : acc), levels[0][1]);

function InsertBar({ onInsert, withCmds = true }) {
  const items = withCmds ? ["{{char}}", "{{user}}", "*การกระทำ*", "\"บทพูด\""] : ["{{char}}", "{{user}}"];
  return <div className="chip-row mb-8" aria-label="แทรกตัวแปร">{items.map((x) => <Chip key={x} size="sm" onClick={() => onInsert(x)}>{x}</Chip>)}</div>;
}

function Slider({ label, left, right, value, onChange }) {
  return (
    <div className="slider-row">
      <div className="lbl"><span>{label}</span><span>{value}</span></div>
      <input type="range" min={0} max={100} value={value} aria-label={label} onChange={(e) => onChange(Number(e.target.value))} />
      <div className="lbl faint"><span>{left}</span><span>{right}</span></div>
    </div>
  );
}

function ScenePicker({ form, set }) {
  const toast = useToast();
  const personas = useAsync(() => content.personas.list(), []);
  const scenarios = useAsync(() => content.scenarios.list(), []);
  const [modal, setModal] = useState(null);
  const [draft, setDraft] = useState({ name: "", detail: "" });
  const [busy, setBusy] = useState(false);
  const open = (kind) => { setDraft({ name: "", detail: "" }); setModal(kind); };
  const save = async () => {
    setBusy(true);
    try {
      if (modal === "persona") { const p = await content.personas.save({ name: draft.name.trim(), detail: draft.detail }); set({ defaultPersonaId: p.id }); personas.reload(); }
      else { const s = await content.scenarios.save({ name: draft.name.trim(), detail: draft.detail }); set({ defaultScenarioId: s.id }); scenarios.reload(); }
      toast("บันทึกแล้ว", "success");
      setModal(null);
    } catch (e) { toast(e.message, "error"); } finally { setBusy(false); }
  };
  const group = (title, hint, state, field, kind) => (
    <div className="mb-16">
      <div className="row-between"><div><div className="strong">{title}</div><div className="field__hint">{hint}</div></div><Button size="sm" onClick={() => open(kind)}>+ สร้างใหม่</Button></div>
      <div className="stack mt-8" role="radiogroup" aria-label={title}>
        <label className="check"><input type="radio" name={field} checked={!form[field]} onChange={() => set({ [field]: "" })} /> ไม่กำหนด (ผู้เล่นเลือกเอง)</label>
        {state.loading ? <Skeleton height={36} /> : (state.data || []).map((r) => (
          <label key={r.id} className="check"><input type="radio" name={field} checked={form[field] === r.id} onChange={() => set({ [field]: r.id })} /><span><b>{r.name}</b><span className="faint clamp-1" style={{ fontSize: 12, display: "block" }}>{r.detail}</span></span></label>
        ))}
        {!state.loading && !(state.data || []).length ? <span className="faint" style={{ fontSize: 13 }}>ยังไม่มีรายการ กด “สร้างใหม่” ได้เลย</span> : null}
      </div>
    </div>
  );
  return (
    <>
      {group("บทบาทของผู้เล่น (Persona)", "ตัวตนของผู้เล่นที่ตัวละครจะรู้จัก", personas, "defaultPersonaId", "persona")}
      {group("สถานการณ์เริ่มต้น", "ฉากเปิดเรื่องที่ใช้ตอนเริ่มแชท", scenarios, "defaultScenarioId", "scenario")}
      <Modal open={!!modal} onClose={() => setModal(null)} title={modal === "persona" ? "สร้างบทบาทผู้เล่น" : "สร้างสถานการณ์"} footer={<><Button variant="ghost" onClick={() => setModal(null)}>ยกเลิก</Button><Button variant="primary" disabled={busy || !draft.name.trim() || (modal === "scenario" && !draft.detail.trim())} onClick={save}>บันทึก</Button></>}>
        <Input label="ชื่อ *" maxLength={modal === "persona" ? 40 : 60} value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
        <Input multiline label={modal === "persona" ? "รายละเอียด" : "รายละเอียด *"} maxLength={modal === "persona" ? 2000 : 3000} value={draft.detail} onChange={(e) => setDraft({ ...draft, detail: e.target.value })} />
      </Modal>
    </>
  );
}

export default function CharacterWizard({ editId }) {
  const navigate = useNavigate();
  const toast = useToast();
  const [form, setForm] = useState(EMPTY);
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [agree, setAgree] = useState(false);
  const [tagOpen, setTagOpen] = useState(false);
  const [idea, setIdea] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  const [aiMsg, setAiMsg] = useState("");
  const [errors, setErrors] = useState([]);
  const [noCredit, setNoCredit] = useState(false);
  const pricing = useAsync(() => api.coins.pricing().catch(() => null), []);
  const allTags = useAsync(() => content.tags.list(), []);
  const loaded = useAsync(async () => (editId ? content.characters.get(editId) : null), [editId]);
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  useEffect(() => { if (loaded.data) setForm(fromCharacter(loaded.data)); }, [loaded.data]);

  const problems = useMemo(() => {
    const p = [];
    if (!form.name.trim()) p.push([0, "ชื่อตัวละคร"]);
    if (!form.tagline.trim()) p.push([0, "คำโปรย"]);
    if (!form.description.trim()) p.push([0, "คำอธิบายตัวละคร (ให้ AI)"]);
    if (!form.images.length) p.push([1, "รูปโปรไฟล์อย่างน้อย 1 รูป"]);
    if (!form.greeting.trim()) p.push([3, "คำทักทายเริ่มต้น"]);
    return p;
  }, [form]);

  if (editId && loaded.loading) return <Skeleton height={300} radius={16} />;
  if (editId && (loaded.error || (loaded.data && !loaded.data.isOwner))) {
    return <EmptyState icon="🔒" title="แก้ไขตัวละครนี้ไม่ได้" action={<Link className="btn" to="/me">กลับโปรไฟล์</Link>}>{loaded.error || "แก้ไขได้เฉพาะตัวละครของตัวเอง"}</EmptyState>;
  }

  const runAi = async () => {
    if (!idea.trim()) return;
    if ((form.name || form.description) && !window.confirm("AI จะเขียนทับชื่อ คำโปรย คำอธิบาย และคำทักทายที่กรอกไว้ ต้องการต่อหรือไม่?")) return;
    setAiBusy(true);
    setAiMsg("");
    try {
      const d = await content.characters.aiDraft(idea.trim());
      set({ name: d.name || form.name, tagline: d.tagline || form.tagline, description: d.description || form.description, greeting: d.greeting || form.greeting, flag: FLAG_FROM_AI[d.flag] || form.flag, tags: d.tags?.length ? d.tags.slice(0, 12) : form.tags });
      setAiMsg(d.mock ? "เติมให้แล้ว (โหมดจำลอง) ตรวจและแก้ไขได้ทุกช่อง" : d.simple ? "เติมร่างแบบง่ายให้แล้ว (ยังไม่ได้โหลดโมเดล AI — โหลดที่หน้าตั้งค่าเพื่อให้ AI เขียนละเอียดขึ้น) ตรวจและแก้ไขได้ทุกช่อง" : "AI เติมให้แล้ว ตรวจและแก้ไขได้ทุกช่อง");
    } catch (e) { setAiMsg(e.message); toast(e.message, "error"); } finally { setAiBusy(false); }
  };

  // เผยแพร่ครั้งแรกใช้ 1 สิทธิ์ (ร่าง -> เผยแพร่) ; แก้ไข/เผยแพร่ซ้ำไม่หัก ; super_user ไม่ใช้สิทธิ์ — server เป็นผู้ตัดสิน ตรงนี้แค่แสดงให้ผู้ใช้รู้ล่วงหน้า
  const pr = pricing.data;
  const published = !!editId && !!loaded.data && loaded.data.status !== "draft";
  const willCharge = !!pr && pr.create.required && !pr.isSuper && !published && !loaded.data?.creditUsedAt;
  const credits = pr ? pr.createCredits : 0;

  const save = async (draft) => {
    setErrors([]);
    setNoCredit(false);
    if (draft) {
      if (!form.name.trim()) { setStep(0); return toast("ตั้งชื่อตัวละครก่อนบันทึกร่าง", "error"); }
    } else {
      if (problems.length) { setErrors(problems); setStep(problems[0][0]); return toast("กรอกข้อมูลที่จำเป็นให้ครบก่อนเผยแพร่", "error"); }
      if (!agree) { setStep(STEPS.length - 1); return toast("กรุณายอมรับข้อกำหนดการสร้างตัวละครก่อนเผยแพร่", "error"); }
      if (willCharge && credits < 1) { setStep(STEPS.length - 1); setNoCredit(true); return toast("สิทธิ์สร้างตัวละครไม่พอ — ซื้อสิทธิ์ก่อนเผยแพร่", "error"); }
    }
    setBusy(true);
    try {
      const c = await content.characters.save(toPayload(form, draft));
      toast(draft ? "บันทึกร่างแล้ว" : c.status === "pending" ? "ส่งตรวจสอบแล้ว รออนุมัติจากผู้ดูแล" : "เผยแพร่ตัวละครแล้ว", "success");
      navigate(draft || c.status === "pending" ? "/me?tab=drafts" : `/c/${c.id}`);
    } catch (e) {
      if (e.code === "insufficient_credits") { setStep(STEPS.length - 1); setNoCredit(true); }
      toast(e.message, "error");
    } finally { setBusy(false); }
  };

  const body = [
    // 0 ข้อมูลพื้นฐาน
    <>
      <Card grad className="mb-16">
        <div className="strong">✨ AI ช่วยเขียน (ฟรี)</div>
        <p className="muted" style={{ fontSize: 13 }}>เล่าแนวคิดสั้น ๆ แล้วให้ AI ร่างชื่อ คำโปรย คำอธิบาย และคำทักทายให้ — แก้ไขต่อได้เอง</p>
        <Input multiline className="mt-8" aria-label="แนวคิดตัวละคร" placeholder="เช่น รุ่นพี่วิศวะใจดี ชอบติวหนังสือและเลี้ยงข้าวน้อง ๆ" maxLength={2000} value={idea} onChange={(e) => setIdea(e.target.value)} hint={`${idea.length}/2000`} />
        <div className="row wrap"><Button variant="primary" onClick={runAi} disabled={aiBusy || !idea.trim()}>{aiBusy ? "AI กำลังเขียน..." : "ให้ AI ช่วยเขียน"}</Button>{aiMsg ? <span className="muted" style={{ fontSize: 13 }} role="status">{aiMsg}</span> : null}</div>
      </Card>
      <Input label="ชื่อตัวละคร *" maxLength={40} value={form.name} onChange={(e) => set({ name: e.target.value })} />
      <Counted value={form.name} max={40} />
      <Input label="คำโปรย *" maxLength={100} value={form.tagline} onChange={(e) => set({ tagline: e.target.value })} hint="แสดงบนการ์ด ใส่ {{user}} เพื่อแทนชื่อผู้เล่นได้" />
      <Counted value={form.tagline} max={100} />
      <InsertBar onInsert={(x) => set({ description: form.description + x })} />
      <Input multiline label="คำอธิบายตัวละคร * (ข้อมูลให้ AI — ผู้เล่นไม่เห็น)" style={{ minHeight: 180 }} maxLength={6000} value={form.description} onChange={(e) => set({ description: e.target.value })} />
      <Counted value={form.description} max={6000} />
      <Input multiline label="ประวัติตัวละคร (ไม่บังคับ — แสดงให้ผู้เล่นอ่าน)" maxLength={8000} value={form.bio} onChange={(e) => set({ bio: e.target.value })} />
      <Counted value={form.bio} max={8000} />
    </>,
    // 1 รูป
    <>
      <p className="muted mb-16">รูปแรกคือรูปหลักที่แสดงบนการ์ด เพิ่มได้สูงสุด 5 รูป ถ้ายังไม่มีรูปบันทึกเป็นร่างไว้ก่อนได้</p>
      <ImageUploader value={form.images} onChange={(images) => set({ images })} max={5} type="character" label="เพิ่มรูปตัวละคร" />
      <div className="mt-16"><Soon icon="🎨" title="สร้างรูปด้วย AI">ฟีเจอร์นี้ยังไม่พร้อมใช้งาน</Soon></div>
    </>,
    // 2 บุคลิก
    <>
      <div className="strong mb-8">ความโรแมนติกเริ่มต้น: <span style={{ color: "var(--accent)" }}>{levelOf(ROMANTIC, form.romantic)}</span></div>
      <div className="pill-opt mb-16" role="radiogroup" aria-label="ความโรแมนติกเริ่มต้น">{ROMANTIC.map(([v, l]) => <Chip key={v} active={form.romantic === v} onClick={() => set({ romantic: v })}>{l}</Chip>)}</div>
      <div className="strong mb-8">ความสนิทเริ่มต้น: <span style={{ color: "var(--accent)" }}>{levelOf(INTIMACY, form.intimacy)}</span></div>
      <div className="pill-opt mb-16" role="radiogroup" aria-label="ความสนิทเริ่มต้น">{INTIMACY.map(([v, l]) => <Chip key={v} active={form.intimacy === v} onClick={() => set({ intimacy: v })}>{l}</Chip>)}</div>
      <div className="strong mb-8">บุคลิกการตอบแชท</div>
      <div className="pill-opt mb-8" role="radiogroup" aria-label="บุคลิกการตอบแชท">{[["", "ให้ระบบเลือกให้"], ...PERSONAS.map((p) => [p.id, p.label])].map(([v, l]) => <Chip key={v || "auto"} active={form.persona === v} onClick={() => set({ persona: v })}>{l}</Chip>)}</div>
      <p className="faint mb-16" style={{ fontSize: 12 }}>กำหนดสรรพนามและสำนวนที่ตัวละครใช้ตอบ (เลือกจากชุดคำตอบที่เตรียมไว้)</p>
      <div className="strong mb-8">นิสัย</div>
      <div className="pill-opt mb-16" role="radiogroup" aria-label="นิสัย">{[["extrovert", "Extrovert"], ["ambivert", "Ambivert"], ["introvert", "Introvert"]].map(([v, l]) => <Chip key={v} active={form.introversion === v} onClick={() => set({ introversion: v })}>{l}</Chip>)}</div>
      <Slider label="การรับรู้อารมณ์" left="เย็นชา" right="เข้าใจอารมณ์" value={form.empathy} onChange={(v) => set({ empathy: v })} />
      <Slider label="ความสนใจเรื่องความรัก" left="ไม่โฟกัสรัก" right="เปิดใจรัก" value={form.loveFocus} onChange={(v) => set({ loveFocus: v })} />
      <Slider label="ความซื่อสัตย์" left="เจ้าชู้" right="รักเดียวใจเดียว" value={form.loyalty} onChange={(v) => set({ loyalty: v })} />
    </>,
    // 3 ข้อความแรก
    <>
      <InsertBar onInsert={(x) => set({ greeting: form.greeting + x })} />
      <Input multiline label="คำทักทายเริ่มต้น *" style={{ minHeight: 160 }} maxLength={3000} value={form.greeting} onChange={(e) => set({ greeting: e.target.value })} hint='ใช้ *การกระทำ* และ "บทพูด" ; {{char}} = ชื่อตัวละคร, {{user}} = ชื่อผู้เล่น' />
      <Counted value={form.greeting} max={3000} />
      <div className="strong mb-8">รูปประกอบคำทักทาย (ไม่บังคับ)</div>
      <ImageUploader value={form.greetingImage ? [form.greetingImage] : []} onChange={(l) => set({ greetingImage: l[0] || "" })} max={1} type="character" label="เพิ่มรูปทักทาย" />
    </>,
    // 4 ฉาก
    <ScenePicker form={form} set={set} />,
    // 5 แท็ก
    <>
      <div className="strong mb-8">ระดับเนื้อหา</div>
      <div className="soon" role="note" style={{ borderStyle: "solid" }}><span aria-hidden="true">✅</span><div><b style={{ color: "var(--text)" }}>PG</b> — เว็บนี้ไม่รองรับเนื้อหา 18+ (NC) ตัวละครทุกตัวจะถูกตั้งเป็น PG</div></div>
      <div className="strong mb-8 mt-16">ระดับธง</div>
      <div className="stack mb-16" role="radiogroup" aria-label="ระดับธง">{FLAGS.map(([v, l, d]) => <label key={v} className="check"><input type="radio" name="flag" checked={form.flag === v} onChange={() => set({ flag: v })} /><span><b className={`tag--${v}`}>{l}</b> <span className="faint" style={{ fontSize: 13 }}>{d}</span></span></label>)}</div>
      <div className="row-between mb-8"><div className="strong">แท็ก ({form.tags.length}/12)</div><Button size="sm" onClick={() => setTagOpen(true)}>เลือกแท็ก</Button></div>
      <div className="chip-row mb-16" style={{ flexWrap: "wrap" }}>
        {form.tags.length ? form.tags.map((t) => <Chip key={t} active onClick={() => set({ tags: form.tags.filter((x) => x !== t) })}>{t} ✕</Chip>) : <span className="faint">ยังไม่ได้เลือกแท็ก</span>}
      </div>
      <label className="field"><span className="field__label">ภาษาต้นฉบับ</span>
        <select className="field__control" value={form.baseLanguage} onChange={(e) => set({ baseLanguage: e.target.value })}>{LANGS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
      </label>
      <TagFilter open={tagOpen} onClose={() => setTagOpen(false)} tags={allTags.data || []} selected={form.tags} onChange={(tags) => set({ tags })} max={12} title="แท็กตัวละคร" />
    </>,
    // 6 ความเป็นส่วนตัว
    <>
      <div className="stack" role="radiogroup" aria-label="ความเป็นส่วนตัว">
        {VIS.map(([v, l, d]) => (
          <label key={v} className="check card" style={{ padding: 14, borderColor: form.visibility === v ? "var(--primary)" : undefined }}>
            <input type="radio" name="vis" checked={form.visibility === v} onChange={() => set({ visibility: v })} /><span><b>{l}</b><span className="muted" style={{ display: "block", fontSize: 13 }}>{d}</span></span>
          </label>
        ))}
      </div>
      <p className="faint mt-16" style={{ fontSize: 13 }}>หากระบบเปิดให้ตรวจก่อนเผยแพร่ ตัวละครจะอยู่สถานะ “รออนุมัติ” จนกว่าผู้ดูแลจะอนุมัติ</p>
    </>,
    // 7 ขั้นสูง + เผยแพร่
    <>
      <Soon icon="🔊" title="เสียงตัวละคร (TTS)">เลือกเสียงอ่านออกเสียงข้อความของตัวละคร</Soon>
      <Soon icon="🎙️" title="โคลนเสียง">สร้างเสียงเฉพาะของตัวละครจากตัวอย่างเสียง</Soon>
      <Soon icon="🎵" title="เพลงประกอบตัวละคร">อัปโหลดหรือเลือกเพลงที่เล่นตอนเปิดหน้าตัวละคร</Soon>
      <Soon icon="☺️" title="อีโมจิตัวละคร / ภารกิจ / ตัวละครสนับสนุน" />
      <hr style={{ borderColor: "var(--border)", margin: "20px 0" }} />
      <div className="strong mb-8">ตรวจก่อนเผยแพร่</div>
      {problems.length ? (
        <ul style={{ paddingLeft: 20, margin: 0 }}>{problems.map(([s, m]) => <li key={m}><button type="button" className="act" onClick={() => setStep(s)} style={{ color: "var(--danger)" }}>ยังไม่ได้กรอก: {m}</button></li>)}</ul>
      ) : <p style={{ color: "var(--success)" }}>ข้อมูลครบถ้วนพร้อมเผยแพร่</p>}
      {pr ? (
        <div className={`notice mt-16${noCredit ? " notice--error" : ""}`} role={noCredit ? "alert" : "status"} style={{ flexDirection: "column", alignItems: "stretch" }}>
          <div><b>สิทธิ์สร้างตัวละคร</b> คงเหลือ <b>{credits}</b> สิทธิ์</div>
          <div style={{ fontSize: 13 }}>
            {pr.isSuper ? "ผู้ดูแลระบบเผยแพร่ได้โดยไม่ใช้สิทธิ์"
              : !pr.create.required ? "ขณะนี้เผยแพร่ตัวละครได้ฟรี ไม่ต้องใช้สิทธิ์"
              : willCharge ? `เผยแพร่ตัวละครนี้ใช้ 1 สิทธิ์ (ราคา ${baht(pr.create.price)} ต่อสิทธิ์ ซื้อเป็นแพ็กถูกกว่า) · บันทึกร่างใช้ฟรี · แก้ไขหลังเผยแพร่ไม่หักซ้ำ`
              : "ตัวละครนี้เผยแพร่ครั้งแรกไปแล้ว — แก้ไข/เผยแพร่ซ้ำไม่หักสิทธิ์"}
          </div>
          {noCredit || (willCharge && credits < 1) ? <div><Link className="btn btn--sm btn--primary" to="/coins?tab=credits">ซื้อสิทธิ์สร้างตัวละคร</Link></div> : null}
        </div>
      ) : null}
      <label className="check mt-16"><input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} /> <span>ฉันยืนยันว่าตัวละครเป็นไปตาม <Link to="/terms" target="_blank">ข้อกำหนดและเงื่อนไข</Link> และไม่มีเนื้อหา 18+ หรือเนื้อหาที่ละเมิดผู้อื่น</span></label>
      {errors.length ? <div className="error-text mt-8" role="alert">กรอกให้ครบ: {errors.map((e) => e[1]).join(", ")}</div> : null}
    </>,
  ];

  return (
    <Wizard steps={STEPS} step={step} setStep={setStep} title={editId ? "แก้ไขตัวละคร" : "สร้างตัวละคร"}
      actions={<>
        <Button onClick={() => save(true)} disabled={busy}>บันทึกร่าง</Button>
        {step === STEPS.length - 1 ? <Button variant="primary" onClick={() => save(false)} disabled={busy}>{busy ? "กำลังบันทึก..." : willCharge ? `เผยแพร่ (ใช้ 1 สิทธิ์ · เหลือ ${credits})` : "เผยแพร่"}</Button> : null}
      </>}>
      {body[step]}
    </Wizard>
  );
}
